import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  TrendingUp, 
  Wallet,
  PiggyBank,
  RefreshCcw,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem("prosperMindToken") || sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Format currency
const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Color palette
const COLORS = {
  income: '#1e40af',           // Dark blue for income
  investmentIncome: '#3b82f6', // Lighter blue for investment income
  basicExpenses: '#16a34a',    // Green for basic/survival expenses
  normalExpenses: '#eab308',   // Yellow for normal/lifestyle expenses
  luxuryExpenses: '#dc2626',   // Red for luxury/emotional expenses
  debtPayments: '#f97316',     // Orange for debt payments
  savingsPotential: '#581c87', // Purple matching the savings capacity box (purple-900)
  savingsNegative: '#991b1b',  // Dark red for negative savings
};

const SavingsPotentialModule = ({ onNavigateToMyNumbers }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [myNumbers, setMyNumbers] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [hasData, setHasData] = useState(false);

  // Fetch my numbers data
  const fetchMyNumbers = async () => {
    setIsLoading(true);
    try {
      const [numbersResponse, snapshotsResponse] = await Promise.all([
        axios.get(`${API}/api/my-numbers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/my-numbers/snapshots?limit=10`, { headers: getAuthHeaders() })
      ]);
      
      if (numbersResponse.data) {
        setMyNumbers(numbersResponse.data);
        // Check if user has entered meaningful data
        const hasIncome = (numbersResponse.data.monthly_income || 0) > 0 || 
                          (numbersResponse.data.monthly_investment_income || 0) > 0;
        const hasExpenses = (numbersResponse.data.monthly_expenses || 0) > 0 || 
                            (numbersResponse.data.total_monthly_debt_payments || 0) > 0;
        setHasData(hasIncome || hasExpenses);
      }
      
      if (snapshotsResponse.data && Array.isArray(snapshotsResponse.data)) {
        // Reverse to show oldest first for the chart
        setSnapshots(snapshotsResponse.data.reverse());
      }
    } catch (error) {
      console.error("Error fetching my numbers:", error);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyNumbers();
    
    // Listen for updates from MyNumbersModule
    const handleMyNumbersUpdate = () => {
      fetchMyNumbers();
    };
    
    window.addEventListener('myNumbersUpdated', handleMyNumbersUpdate);
    return () => window.removeEventListener('myNumbersUpdated', handleMyNumbersUpdate);
  }, []);

  // Calculate values
  const calculateValues = () => {
    if (!myNumbers) {
      return {
        income: 0,
        investmentIncome: 0,
        totalIncome: 0,
        expenses: 0,
        basicExpenses: 0,
        normalExpenses: 0,
        luxuryExpenses: 0,
        debtPayments: 0,
        totalExpenses: 0,
        savingsPotential: 0,
        hasAllocation: false
      };
    }

    const income = parseFloat(myNumbers.monthly_income) || 0;
    const investmentIncome = parseFloat(myNumbers.monthly_investment_income) || 0;
    const totalIncome = income + investmentIncome;

    const expenses = parseFloat(myNumbers.monthly_expenses) || 0;
    const debtPayments = parseFloat(myNumbers.total_monthly_debt_payments) || 0;
    const totalExpenses = expenses + debtPayments;

    // Get expense allocation percentages
    const allocation = myNumbers.expense_allocation || {};
    const basicPct = allocation.basic_survival || 0;
    const normalPct = allocation.normal_lifestyle || 0;
    const luxuryPct = allocation.luxury_emotional || 0;
    const hasAllocation = basicPct > 0 || normalPct > 0 || luxuryPct > 0;

    // Calculate expense breakdown based on allocation
    const basicExpenses = hasAllocation ? (expenses * basicPct / 100) : 0;
    const normalExpenses = hasAllocation ? (expenses * normalPct / 100) : 0;
    const luxuryExpenses = hasAllocation ? (expenses * luxuryPct / 100) : expenses;

    const savingsPotential = totalIncome - totalExpenses;

    return {
      income,
      investmentIncome,
      totalIncome,
      expenses,
      basicExpenses,
      normalExpenses,
      luxuryExpenses,
      debtPayments,
      totalExpenses,
      savingsPotential,
      hasAllocation
    };
  };

  const values = calculateValues();

  // Prepare chart data - stacked bars
  const chartData = [
    {
      category: t('savingsChart.incomeLabel'),
      income: values.income,
      investmentIncome: values.investmentIncome,
      total: values.totalIncome
    },
    {
      category: t('savingsChart.expensesLabel'),
      basicExpenses: values.basicExpenses,
      normalExpenses: values.normalExpenses,
      luxuryExpenses: values.luxuryExpenses,
      debtPayments: values.debtPayments,
      total: values.totalExpenses,
      hasAllocation: values.hasAllocation
    },
    {
      category: t('savingsChart.savingsLabel'),
      savingsPotential: Math.abs(values.savingsPotential),
      isNegative: values.savingsPotential < 0,
      total: values.savingsPotential
    }
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => {
            if (entry.value > 0) {
              let displayName = entry.name;
              if (entry.name === 'income') displayName = t('savingsChart.incomeFromWork');
              if (entry.name === 'investmentIncome') displayName = t('savingsChart.investmentIncome');
              if (entry.name === 'basicExpenses') displayName = t('savingsChart.basicSurvival');
              if (entry.name === 'normalExpenses') displayName = t('savingsChart.normalLifestyle');
              if (entry.name === 'luxuryExpenses') displayName = t('savingsChart.luxuryEmotional');
              if (entry.name === 'debtPayments') displayName = t('savingsChart.debtPayments');
              if (entry.name === 'savingsPotential') {
                displayName = payload[0]?.payload?.isNegative ? t('savingsChart.deficit') : t('savingsChart.savingsCapacity');
              }
              
              return (
                <p key={index} style={{ color: entry.fill }} className="text-sm">
                  {displayName}: {formatCurrency(entry.value)}
                </p>
              );
            }
            return null;
          })}
          {label !== t('savingsChart.savingsLabel') && (
            <p className="text-gray-400 text-xs mt-1 border-t border-gray-700 pt-1">
              {t('savingsChart.total')}: {formatCurrency(payload[0]?.payload?.total)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="savings-potential-loading">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!hasData) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="savings-potential-no-data">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
            <PiggyBank className="w-6 h-6 text-green-500" />
            {t('savingsChart.noDataTitle')}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {t('savingsChart.noDataDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">
              {t('savingsChart.noDataMessage')}
            </p>
            {onNavigateToMyNumbers && (
              <Button
                onClick={onNavigateToMyNumbers}
                className="bg-green-600 hover:bg-green-700"
              >
                <Wallet className="w-4 h-4 mr-2" />
                {t('savingsChart.enterData')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-testid="savings-potential-module">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
              <PiggyBank className="w-6 h-6 text-green-500" />
              {t('savingsChart.title')}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {t('savingsChart.description')}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchMyNumbers}
            className="text-gray-400 hover:text-white"
          >
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/50">
            <p className="text-blue-400 text-xs uppercase tracking-wide mb-1">{t('savingsChart.incomeLabel')}</p>
            <p className="text-white text-xl font-bold">{formatCurrency(values.totalIncome)}</p>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-800"></span>
                <span className="text-gray-400">{t('savingsChart.incomeFromWork')}: {formatCurrency(values.income)}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs mt-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-400">{t('savingsChart.rentsAndDividends')}: {formatCurrency(values.investmentIncome)}</span>
              </span>
            </div>
          </div>

          <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
            <p className="text-red-400 text-xs uppercase tracking-wide mb-1">{t('savingsChart.expensesLabel')}</p>
            <p className="text-white text-xl font-bold">{formatCurrency(values.totalExpenses)}</p>
            {values.hasAllocation ? (
              <>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-gray-400">{t('savingsChart.basicSurvival')}: {formatCurrency(values.basicExpenses)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    <span className="text-gray-400">{t('savingsChart.normalLifestyle')}: {formatCurrency(values.normalExpenses)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span className="text-gray-400">{t('savingsChart.luxuryEmotional')}: {formatCurrency(values.luxuryExpenses)}</span>
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-600"></span>
                  <span className="text-gray-400">{t('common.total')}: {formatCurrency(values.expenses)}</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs mt-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-gray-400">{t('savingsChart.debtPayments')}: {formatCurrency(values.debtPayments)}</span>
              </span>
            </div>
          </div>

          <div className={`rounded-lg p-4 border ${
            values.savingsPotential >= 0 
              ? 'bg-purple-900/30 border-purple-700/50' 
              : 'bg-red-900/40 border-red-600/50'
          }`}>
            <p className={`text-xs uppercase tracking-wide mb-1 ${
              values.savingsPotential >= 0 ? 'text-purple-400' : 'text-red-400'
            }`}>
              {values.savingsPotential >= 0 ? t('savingsChart.savingsCapacity') : t('savingsChart.monthlyDeficit')}
            </p>
            <p className="text-white text-xl font-bold">
              {values.savingsPotential >= 0 ? '+' : ''}{formatCurrency(values.savingsPotential)}
            </p>
            <div className="mt-2">
              {values.savingsPotential >= 0 ? (
                <TrendingUp className="w-5 h-5 text-purple-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
            </div>
          </div>
        </div>

        {/* Stacked Bar Chart */}
        <div className="bg-gray-800/50 rounded-xl p-4">
          {/* Fixed container for mobile Safari - using explicit height instead of ResponsiveContainer percentage */}
          <div className="w-full overflow-x-auto" style={{ minHeight: '320px' }}>
            <div style={{ width: '100%', minWidth: '400px', height: '300px' }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                barCategoryGap="20%"
                barSize={60}
              >
              <XAxis 
                dataKey="category" 
                tick={{ fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                content={({ payload }) => {
                  // Define the desired order
                  const order = [
                    'income',
                    'investmentIncome', 
                    'basicExpenses',
                    'normalExpenses',
                    'luxuryExpenses',
                    'debtPayments',
                    'savingsPotential'
                  ];
                  
                  const labels = {
                    income: t('savingsChart.incomeFromWork'),
                    investmentIncome: t('savingsChart.rentsAndDividends'),
                    basicExpenses: t('savingsChart.basicSurvival'),
                    normalExpenses: t('savingsChart.normalLifestyle'),
                    luxuryExpenses: t('savingsChart.luxuryEmotional'),
                    debtPayments: t('savingsChart.debtPayments'),
                    savingsPotential: t('savingsChart.savingsCapacity')
                  };
                  
                  // Sort payload by desired order
                  const sortedPayload = [...payload].sort((a, b) => {
                    return order.indexOf(a.dataKey) - order.indexOf(b.dataKey);
                  });
                  
                  return (
                    <div className="flex flex-wrap justify-center gap-4 pt-2">
                      {sortedPayload.map((entry, index) => (
                        <div key={`legend-${index}`} className="flex items-center gap-1.5">
                          <div 
                            className="w-3 h-3 rounded-sm" 
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-gray-300 text-sm">
                            {labels[entry.dataKey] || entry.dataKey}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <ReferenceLine y={0} stroke="#6b7280" />
              
              {/* Income bars (blue tones) */}
              <Bar 
                dataKey="income" 
                stackId="a" 
                fill={COLORS.income}
                radius={[0, 0, 0, 0]}
                name="income"
              />
              <Bar 
                dataKey="investmentIncome" 
                stackId="a" 
                fill={COLORS.investmentIncome}
                radius={[4, 4, 0, 0]}
                name="investmentIncome"
              />
              
              {/* Expense bars - stacked by allocation (green/yellow/red) + debt (orange) */}
              {/* Basic/Survival (bottom - green) */}
              <Bar 
                dataKey="basicExpenses" 
                stackId="a" 
                fill={COLORS.basicExpenses}
                radius={[0, 0, 0, 0]}
                name="basicExpenses"
              />
              {/* Normal/Lifestyle (middle - yellow) */}
              <Bar 
                dataKey="normalExpenses" 
                stackId="a" 
                fill={COLORS.normalExpenses}
                radius={[0, 0, 0, 0]}
                name="normalExpenses"
              />
              {/* Luxury/Emotional (top before debt - red) */}
              <Bar 
                dataKey="luxuryExpenses" 
                stackId="a" 
                fill={COLORS.luxuryExpenses}
                radius={[0, 0, 0, 0]}
                name="luxuryExpenses"
              />
              {/* Debt Payments (top - orange) */}
              <Bar 
                dataKey="debtPayments" 
                stackId="a" 
                fill={COLORS.debtPayments}
                radius={[4, 4, 0, 0]}
                name="debtPayments"
              />
              
              {/* Savings bar (green or red based on value) */}
              <Bar 
                dataKey="savingsPotential" 
                stackId="a"
                fill={COLORS.savingsPotential}
                radius={[4, 4, 0, 0]}
                name="savingsPotential"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.isNegative ? COLORS.savingsNegative : COLORS.savingsPotential} 
                  />
                ))}
              </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          </div>
        </div>

        {/* Insights */}
        <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            Quick Insights
          </h4>
          <div className="space-y-3 text-sm text-gray-400">
            {/* 1. Savings Potential */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></span>
              <p>
                {values.savingsPotential >= 0 ? (
                  <>
                    You can potentially save <span className="text-purple-400 font-medium">{formatCurrency(values.savingsPotential)}</span> per month, 
                    which is <span className="text-purple-400 font-medium">{values.totalIncome > 0 ? ((values.savingsPotential / values.totalIncome) * 100).toFixed(1) : 0}%</span> of your income.
                  </>
                ) : (
                  <>
                    You're spending <span className="text-red-400 font-medium">{formatCurrency(Math.abs(values.savingsPotential))}</span> more than you earn each month 
                    (<span className="text-red-400 font-medium">{values.totalIncome > 0 ? ((Math.abs(values.savingsPotential) / values.totalIncome) * 100).toFixed(1) : 0}%</span> over budget).
                  </>
                )}
              </p>
            </div>
            
            {/* 2. Debt as % of income */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0"></span>
              <p>
                <span className="text-orange-400 font-medium">{values.totalIncome > 0 ? ((values.debtPayments / values.totalIncome) * 100).toFixed(1) : 0}%</span> of your income goes to pay for debt 
                (<span className="text-orange-400 font-medium">{formatCurrency(values.debtPayments)}</span>/month).
              </p>
            </div>
            
            {/* 3. Investment income coverage */}
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></span>
              <p>
                Your investment income (<span className="text-blue-400 font-medium">{formatCurrency(values.investmentIncome)}</span>) covers 
                <span className="text-blue-400 font-medium"> {values.totalExpenses > 0 ? ((values.investmentIncome / values.totalExpenses) * 100).toFixed(1) : 0}%</span> of your total expenses 
                and represents <span className="text-blue-400 font-medium">{values.totalIncome > 0 ? ((values.investmentIncome / values.totalIncome) * 100).toFixed(1) : 0}%</span> of your total income.
              </p>
            </div>
          </div>
        </div>

        {/* Edit Data Button */}
        {onNavigateToMyNumbers && (
          <div className="text-center pt-2">
            <Button
              variant="outline"
              onClick={onNavigateToMyNumbers}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Update My Numbers
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavingsPotentialModule;
